import React from 'react'
import { connect } from 'react-redux'
import {
  Button,
  Content,
  Dialog,
  DialogContainer,
  Divider,
  Footer,
  Heading,
  Well,
} from '@adobe/react-spectrum'
import { SPECTRUM_THEME_CLASS } from '../../themes/spectrum-theme'
import { APP_CONFIG } from '../../config'
import type { SubscriptionPlanModel } from '../../types/generated/models/subscription-plan-model'
import { getTranslate } from 'react-localize-redux'
import { getSubscriptionFeatureTranslationKey, translateSubscriptionFeature } from './subscription-feature-translations'
import { basicFeatureKeys, plusBenefits, getUpgradeStartingPrice } from './subscription-upgrade-content'
import './subscription-upgrade-dialog.css'

interface SubscriptionUpgradeDialogProps {
  open: boolean
  onClose: () => void
  onOpenWizard?: () => void
  readOnly?: boolean
  // Redux state
  subscriptionPlans: SubscriptionPlanModel[]
  selectedCurrency?: string
  selectedLanguage?: string
  translate: (key: string, data?: Record<string, unknown>) => string
}

export const SubscriptionUpgradeDialog: React.FC<SubscriptionUpgradeDialogProps> = ({
  open,
  onClose,
  onOpenWizard,
  readOnly = false,
  subscriptionPlans,
  selectedCurrency = 'GBP',
  selectedLanguage = 'en-GB',
  translate,
}) => {
  const plusPlan = subscriptionPlans?.find(plan => plan.id === 'plus')
  const plusFeatures = plusPlan?.features || []
  const enabledFeatureKeys = new Set(plusFeatures.map(getSubscriptionFeatureTranslationKey))
  const benefits = plusBenefits.filter(benefit => enabledFeatureKeys.has(benefit.featureKey))
  const additionalFeatures = plusFeatures.filter(feature => {
    const key = getSubscriptionFeatureTranslationKey(feature)
    return key !== 'subscription_dialog_all_basic_features' && !plusBenefits.some(benefit => benefit.featureKey === key)
  })
  const startingPrice = getUpgradeStartingPrice(plusPlan?.pricing || [], selectedCurrency)
  const locale = selectedLanguage === 'jp-JP' ? 'ja-JP' : selectedLanguage || 'en-GB'
  const formatPrice = (amount: number, currency: string) => new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount / 100)

  const handleUpgradeClick = () => {
    if (readOnly || !plusPlan || !onOpenWizard) return
    onClose()
    setTimeout(() => {
      onOpenWizard?.()
    }, 300)
  }

  return (
    <DialogContainer onDismiss={onClose}>
      {open && (
        <Dialog size="L" UNSAFE_className={`${SPECTRUM_THEME_CLASS} subscription-upgrade-dialog`}>
          <>
            <Heading level={2}>{translate('subscription_upgrade_choose_subscription')}</Heading>
            <Divider />
            <Content>
              <div className="subscription-upgrade-plans">
                <Well UNSAFE_className="subscription-upgrade-plan subscription-upgrade-basic">
                  <div className="subscription-upgrade-card-content">
                    <div className="subscription-upgrade-badge subscription-upgrade-current-badge">
                      {translate('subscription_upgrade_current_plan')}
                    </div>
                    <h3 className="subscription-upgrade-plan-title">AnyWARE Basic</h3>
                    <p className="subscription-upgrade-description">
                      {translate('subscription_upgrade_basic_description')}
                    </p>
                    <div className="subscription-upgrade-price">
                      {translate('subscription_upgrade_monthly_per_device', {
                        amount: formatPrice(0, startingPrice?.currency || selectedCurrency),
                      })}
                    </div>
                    <ul className="subscription-upgrade-basic-features">
                      {basicFeatureKeys.map(key => (
                        <li key={key}>{translate(key)}{key === 'subscription_upgrade_tester_health_check' ? '*' : ''}</li>
                      ))}
                    </ul>
                    <p className="subscription-upgrade-footnote">*{translate('subscription_upgrade_lantek_only')}</p>
                  </div>
                  <Button variant="secondary" isDisabled width="100%" marginTop="size-200">
                    {translate('subscription_upgrade_current_plan')}
                  </Button>
                </Well>

                <Well UNSAFE_className="subscription-upgrade-plan subscription-upgrade-plus">
                  <div className="subscription-upgrade-card-content">
                    <div className="subscription-upgrade-badge subscription-upgrade-plus-badge">
                      {translate('subscription_upgrade_upgrade')}
                    </div>
                    <h3 className="subscription-upgrade-plan-title">AnyWARE Plus</h3>
                    <p className="subscription-upgrade-description">
                      {translate('subscription_upgrade_plus_description')}
                    </p>
                    <p className="subscription-upgrade-availability">
                      {translate('subscription_upgrade_supported_devices')}
                    </p>
                    <div className="subscription-upgrade-price">
                      {startingPrice
                        ? translate('subscription_upgrade_starting_price', {
                          amount: formatPrice(startingPrice.amount, startingPrice.currency),
                        })
                        : translate('subscription_upgrade_per_device_pricing')}
                    </div>
                    <div className="subscription-upgrade-benefits">
                      {benefits.map(benefit => (
                        <section key={benefit.featureKey} className={benefit.featureKey === 'subscription_dialog_geolocation_lock'
                          ? 'subscription-upgrade-location' : undefined}>
                          <div>
                            <h4 className="subscription-upgrade-benefit-title">{translate(benefit.titleKey)}</h4>
                            <ul className="subscription-upgrade-benefit-list">
                              {benefit.descriptionKeys.map(key => <li key={key}>{translate(key)}</li>)}
                            </ul>
                            {benefit.featureKey === 'subscription_dialog_geolocation_lock' && (
                              <>
                                <h4 className="subscription-upgrade-benefit-title subscription-upgrade-lock-title">
                                  {translate('subscription_upgrade_device_lock')}
                                  {!APP_CONFIG.deviceLockEnabled && (
                                    <span className="subscription-upgrade-coming-soon">
                                      {translate('subscription_upgrade_coming_soon')}
                                    </span>
                                  )}
                                </h4>
                                <ul className="subscription-upgrade-benefit-list">
                                  <li>{translate('subscription_upgrade_device_lock_protect')}</li>
                                  <li>{translate('subscription_upgrade_device_lock_reduce_risk')}</li>
                                </ul>
                              </>
                            )}
                          </div>
                          {benefit.featureKey === 'subscription_dialog_geolocation_lock' && (
                            <figure className="subscription-upgrade-preview">
                              <iframe
                                src={`https://www.youtube-nocookie.com/embed/YS__XMGWmiE?autoplay=0&playsinline=1&rel=0&hl=${encodeURIComponent(locale)}`}
                                title={translate('subscription_upgrade_geolocation')}
                                width="640"
                                height="360"
                                loading="lazy"
                                allow="encrypted-media; picture-in-picture; fullscreen"
                                allowFullScreen
                                referrerPolicy="strict-origin-when-cross-origin"
                              />
                              <figcaption>{translate('subscription_upgrade_geolocation')}</figcaption>
                            </figure>
                          )}
                        </section>
                      ))}
                      {additionalFeatures.length > 0 && (
                        <ul className="subscription-upgrade-benefit-list">
                          {additionalFeatures.map(feature => <li key={feature}>{translateSubscriptionFeature(feature, translate)}</li>)}
                        </ul>
                      )}
                    </div>
                  </div>
                </Well>
              </div>
            </Content>
            <Divider />
            <Footer>
              <div className="subscription-upgrade-footer">
                <Button variant="secondary" onPress={onClose}>
                  {translate('general_close')}
                </Button>
                <Button
                  variant="accent"
                  width="100%"
                  UNSAFE_className="subscription-upgrade-action"
                  onPress={handleUpgradeClick}
                  isDisabled={readOnly || !plusPlan || !onOpenWizard}
                >
                  {translate(readOnly ? 'subscription_upgrade_view_only' : 'subscription_upgrade_upgrade_now')}
                </Button>
              </div>
            </Footer>
          </>
        </Dialog>
      )}
    </DialogContainer>
  )
}

const mapStateToProps = (state: any) => ({
  subscriptionPlans: state.appSettingReducer.subscriptionPlans,
  selectedCurrency: state.subscriptionReducer.selectedCurrency,
  selectedLanguage: state.languageReducer.selectedLanguage,
  translate: getTranslate(state.localeReducer),
})

export default connect(mapStateToProps)(SubscriptionUpgradeDialog)
